import "../css/header.scss";
import React, { useContext, useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  FiHeart,
  FiChevronDown,
  FiMenu,
  FiSearch,
  FiShoppingBag,
  FiX,
} from "react-icons/fi";
import { CartContext } from "./CartContext";
import siteConfig from "../config/siteConfig";

const primaryNavigation = siteConfig.navigation;
const moreNavigation = [
  { label: "FAQs", path: "/faqs" },
  { label: "All Policies", path: "/policies" },
  { label: "Privacy", path: "/privacy" },
  { label: "Cookie Policy", path: "/cookies" },
  { label: "Terms", path: "/terms" },
  { label: "Shipping", path: "/shipping" },
  { label: "Returns", path: "/returns" },
  { label: "Contact", path: "/contact" },
];

function Header() {
  const { cartItems = [], cartCount, wishlist = [] } = useContext(CartContext);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [isMoreOpen, setIsMoreOpen] = useState(false);
  const searchInputRef = useRef(null);
  const menuTriggerRef = useRef(null);
  const menuDialogRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  const visibleCartCount = Number.isFinite(cartCount)
    ? cartCount
    : cartItems.reduce((total, item) => total + Number(item.quantity || 0), 0);
  const wishlistCount = Array.isArray(wishlist) ? wishlist.length : 0;

  useEffect(() => {
    setIsMenuOpen(false);
    setIsSearchOpen(false);
    setIsMoreOpen(false);
    window.scrollTo({ top: 0, left: 0 });

    if (!location.pathname.startsWith("/shopDetails/")) {
      const shopTitle = location.search.includes("wishlist=true")
        ? `Wishlist · ${siteConfig.brandName}`
        : location.search.includes("collection=best-sellers")
          ? `Best sellers · ${siteConfig.brandName}`
          : `Shop all · ${siteConfig.brandName}`;
      const routeMetadata = siteConfig.routeSeo?.[location.pathname] || siteConfig.seo;
      document.title = location.pathname === "/shop"
        ? shopTitle
        : routeMetadata.title;
      const descriptionMeta = document.querySelector('meta[name="description"]');
      const shopDescription = location.search.includes("wishlist=true")
        ? "Review the plant-powered rituals saved to your wishlist."
        : location.search.includes("collection=best-sellers")
          ? "Shop customer-favorite plant-powered hair and body rituals."
          : "Browse the complete collection of plant-powered hair and body rituals.";
      descriptionMeta?.setAttribute(
        "content",
        location.pathname === "/shop" ? shopDescription : routeMetadata.description
      );

      const pageTitle = location.pathname === "/shop" ? shopTitle : routeMetadata.title;
      const pageDescription = location.pathname === "/shop"
        ? shopDescription
        : routeMetadata.description;
      document.querySelector('meta[property="og:title"]')
        ?.setAttribute("content", pageTitle);
      document.querySelector('meta[property="og:description"]')
        ?.setAttribute("content", pageDescription);
    }

    document.documentElement.lang = siteConfig.locale;
    document.querySelector('meta[property="og:locale"]')
      ?.setAttribute("content", siteConfig.locale.replace("-", "_"));

    const structuredDataElement = document.getElementById("site-structured-data");
    if (structuredDataElement) {
      structuredDataElement.textContent = JSON.stringify(siteConfig.structuredData);
    }
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (isSearchOpen) {
      searchInputRef.current?.focus();
    }
  }, [isSearchOpen]);

  useEffect(() => {
    if (!isSearchOpen) return undefined;

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setIsSearchOpen(false);
      }
    };

    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [isSearchOpen]);

  useEffect(() => {
    if (!isMenuOpen) return undefined;

    const dialog = menuDialogRef.current;
    const menuTrigger = menuTriggerRef.current;
    const previousOverflow = document.body.style.overflow;
    const focusableSelector =
      'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';

    document.body.style.overflow = "hidden";
    window.requestAnimationFrame(() => {
      dialog?.querySelector(focusableSelector)?.focus();
    });

    const handleMenuKeyDown = (event) => {
      if (event.key === "Escape") {
        setIsMenuOpen(false);
        return;
      }
      if (event.key !== "Tab" || !dialog) return;

      const focusable = [...dialog.querySelectorAll(focusableSelector)];
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    window.addEventListener("keydown", handleMenuKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleMenuKeyDown);
      menuTrigger?.focus();
    };
  }, [isMenuOpen]);

  const toTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const toggleMenu = () => {
    setIsSearchOpen(false);
    setIsMoreOpen(false);
    setIsMenuOpen((isOpen) => !isOpen);
  };

  const toggleSearch = () => {
    setIsMenuOpen(false);
    setIsSearchOpen((isOpen) => !isOpen);
  };

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    const query = searchTerm.trim();

    navigate(query ? `/shop?search=${encodeURIComponent(query)}` : "/shop");
    setIsSearchOpen(false);
    toTop();
  };

  const closeNavigation = () => {
    setIsMenuOpen(false);
    setIsSearchOpen(false);
    setIsMoreOpen(false);
    toTop();
  };

  const isCurrentNavigationItem = (item) => {
    const [itemPath, itemQuery = ""] = item.path.split("?");
    if (location.pathname !== itemPath) return false;
    if (!itemQuery) return !location.search;

    const currentParams = new URLSearchParams(location.search);
    const itemParams = new URLSearchParams(itemQuery);
    return [...itemParams.entries()].every(
      ([key, value]) => currentParams.get(key) === value
    );
  };

  return (
    <header className="site-header">
      <div className="announcement-bar" role="status">
        <p>{siteConfig.announcement}</p>
      </div>

      <div className="header-surface">
        <div className="header-primary">
          <div className="header-primary__start">
            <button
              ref={menuTriggerRef}
              type="button"
              className="header-icon-button header-menu-trigger"
              aria-label={isMenuOpen ? "Close navigation menu" : "Open navigation menu"}
              aria-controls="mobile-navigation"
              aria-expanded={isMenuOpen}
              onClick={toggleMenu}
            >
              {isMenuOpen ? <FiX aria-hidden="true" /> : <FiMenu aria-hidden="true" />}
            </button>

            <button
              type="button"
              className="header-search-trigger"
              aria-label={isSearchOpen ? "Close search" : "Search products"}
              aria-controls="header-search-panel"
              aria-expanded={isSearchOpen}
              onClick={toggleSearch}
            >
              <FiSearch aria-hidden="true" />
              <span>Search</span>
            </button>
          </div>

          <Link
            to="/"
            className="header-wordmark"
            aria-label={`${siteConfig.brandName} home`}
            onClick={closeNavigation}
          >
            <img src={siteConfig.logo.src} alt={siteConfig.brandName} />
          </Link>

          <div className="header-actions" aria-label="Customer tools">
            <Link
              to="/shop?wishlist=true"
              className="header-action"
              aria-label={`Wishlist, ${wishlistCount} saved item${
                wishlistCount === 1 ? "" : "s"
              }`}
              onClick={closeNavigation}
            >
              <FiHeart aria-hidden="true" />
              <span className="header-action__count" aria-hidden="true">
                {wishlistCount}
              </span>
            </Link>

            <Link
              to="/cartpage"
              className="header-action"
              aria-label={`Shopping bag, ${visibleCartCount} item${
                visibleCartCount === 1 ? "" : "s"
              }`}
              onClick={closeNavigation}
            >
              <FiShoppingBag aria-hidden="true" />
              <span className="header-action__count" aria-hidden="true">
                {visibleCartCount}
              </span>
            </Link>
          </div>
        </div>

        <nav className="desktop-navigation" aria-label="Primary navigation">
          {primaryNavigation.map((item) => (
            <Link
              key={item.label}
              to={item.path}
              onClick={closeNavigation}
              aria-current={isCurrentNavigationItem(item) ? "page" : undefined}
            >
              {item.label}
            </Link>
          ))}
          <div className="desktop-more">
            <button
              type="button"
              aria-expanded={isMoreOpen}
              aria-controls="desktop-more-menu"
              onClick={() => setIsMoreOpen((open) => !open)}
            >
              More <FiChevronDown aria-hidden="true" />
            </button>
            {isMoreOpen && (
              <div className="desktop-more__menu" id="desktop-more-menu">
                {moreNavigation.map((item) => (
                  <Link key={item.path} to={item.path} onClick={closeNavigation}>
                    {item.label}
                  </Link>
                ))}
              </div>
            )}
          </div>
        </nav>

        {isSearchOpen && (
          <div className="header-search-panel" id="header-search-panel">
            <form
              className="header-search-form"
              role="search"
              onSubmit={handleSearchSubmit}
            >
              <FiSearch aria-hidden="true" />
              <label className="visually-hidden" htmlFor="site-search">
                Search products
              </label>
              <input
                ref={searchInputRef}
                id="site-search"
                type="search"
                value={searchTerm}
                placeholder="Search shampoos, conditioners and treatments"
                autoComplete="off"
                onChange={(event) => setSearchTerm(event.target.value)}
              />
              <button type="submit" className="search-submit">
                Search
              </button>
              <button
                type="button"
                className="search-close"
                aria-label="Close search"
                onClick={() => setIsSearchOpen(false)}
              >
                <FiX aria-hidden="true" />
              </button>
            </form>
          </div>
        )}
      </div>

      {isMenuOpen && (
        <>
          <button
            type="button"
            className="mobile-navigation-overlay"
            aria-label="Close navigation menu"
            onClick={() => setIsMenuOpen(false)}
          />
          <div
            ref={menuDialogRef}
            className="mobile-navigation"
            id="mobile-navigation"
            aria-label="Mobile navigation"
            aria-modal="true"
            role="dialog"
          >
            <div className="mobile-navigation__header">
              <img src={siteConfig.logo.src} alt={siteConfig.brandName} />
              <button
                type="button"
                className="header-icon-button"
                aria-label="Close navigation menu"
                onClick={() => setIsMenuOpen(false)}
              >
                <FiX aria-hidden="true" />
              </button>
            </div>

            <nav aria-label="Mobile primary navigation">
              {primaryNavigation.map((item) => (
                <Link
                  key={item.label}
                  to={item.path}
                  onClick={closeNavigation}
                  aria-current={isCurrentNavigationItem(item) ? "page" : undefined}
                >
                  {item.label}
                  <span aria-hidden="true">→</span>
                </Link>
              ))}
              <span className="mobile-navigation__more-label">More</span>
              {moreNavigation.map((item) => (
                <Link key={item.path} to={item.path} onClick={closeNavigation}>
                  {item.label}
                  <span aria-hidden="true">→</span>
                </Link>
              ))}
            </nav>

            <button
              type="button"
              className="mobile-navigation__search"
              onClick={() => {
                setIsMenuOpen(false);
                setIsSearchOpen(true);
              }}
            >
              <FiSearch aria-hidden="true" />
              Search products
            </button>

            <p>{siteConfig.tagline}</p>
          </div>
        </>
      )}
    </header>
  );
}

export default Header;
